import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/Dashboard.js (actual add-client modal: name + email required,
//     contact name + internal reference optional; button label "Add client")
//   - src/lib/supabase.js sendClientRequestEmail uses client.name as
//     {{client_name}} (NOT contact_name) -- old article wrongly promised
//     personalised greeting using contact_name
//   - src/pages/Settings.js (billing snapshot on the 28th, archived excluded)
//   - supabase/functions/update-client-count/index.ts (proration_behavior=none
//     -- there is NO pro-rating)
// User confirmed:
//   - Multiple contacts per client NOT on roadmap (removed)
//   - 90-day retention NOT enforced (removed)
//   - 7-year retention IS real (per Terms section 9), but not relevant to
//     a "how to add a client" article. Belongs in the cancellation article.
// Removed:
//   - "Personalised greeting (Hi Sarah)" claim -- backend uses client.name
//   - "Multiple contacts per client is on the roadmap" promise
//   - "Pro-rated to the day" billing claim (proration is set to none)
// Added:
//   - Internal reference field (was missing from old article)
//   - Accurate billing-date behaviour (28th-of-month snapshot)
// Pass 2: re-read Dashboard add-client modal end to end. No additional
// features the article was misleading users by omitting.

export default {
  title:   'Adding your first client',
  summary: 'How to create a client record, what details Receipt Chaser needs, and how clients count toward your billing.',
  body: (
    <>
      <P>
        A client in Receipt Chaser is the entity you do bookkeeping for.
        Usually a limited company, sole trader, or partnership. It's not
        the same as the person you actually email. The person is the{' '}
        <strong>contact</strong>, optional and stored alongside the client
        record.
      </P>

      <Callout variant="tip" title="A real-world example">
        Bluewater Plumbing Services is the client. Graham Boot is the
        contact, because he's the director who handles the books.
        You're chasing Graham for Bluewater's receipts.
      </Callout>

      <H>Step by step</H>
      <OL>
        <Li>
          On the dashboard, click <Code>+ Add client</Code>.
        </Li>
        <Li>
          Enter the <strong>Client / company name</strong>. Use the legal
          entity name as it appears in your accounting software, so your
          records line up. Example: <em>Bluewater Plumbing Services</em>,
          not just <em>Bluewater</em>. This field is required.
        </Li>
        <Li>
          Enter the <strong>Email address</strong>. This is where every
          receipt request will be sent. Get this right at the start. A
          wrong email is the most common reason clients say they didn't
          get the request. This field is required.
        </Li>
        <Li>
          Optionally, add a <strong>Contact name</strong>. Stored on the
          client record for your own reference and shown in the dashboard
          search results. It is not currently used in the email greeting.
        </Li>
        <Li>
          Optionally, add an <strong>Internal reference</strong>. Useful
          if you use codes like <Code>BPS</Code> to match clients
          with your practice management system. Searchable from the
          dashboard. Maximum 50 characters.
        </Li>
        <Li>
          Click <Code>Add client</Code>. The client now appears on your
          dashboard, ready to receive their first request.
        </Li>
      </OL>

      <Screenshot
        src="/support-screenshots/02 - add new client.png"
        alt="The Add new client modal showing the four fields: Client / company name, Email address, Contact name (optional), and Internal reference (optional). The Add client button is at the bottom right."
        caption="The Add new client modal showing the four fields. Required fields have no marker; optional fields are labelled."
        width={480}
      />

      <H>What if a client has more than one person who handles receipts?</H>
      <P>
        Each client record holds one email address, but the request
        link itself works for anyone who has it. When you create the
        request, pick <strong>Copy link to send myself</strong> instead
        of <em>Send email automatically</em>. Receipt Chaser then
        copies the upload link to your clipboard, and you can email it
        manually to as many people as you need. The same link works
        for all of them, with their uploads landing in the same
        request.
      </P>

      <H>What about clients I've stopped working with?</H>
      <P>
        Don't delete them. Archive them instead. Archived clients are
        hidden from your main dashboard but their history (every request,
        every uploaded receipt) is kept. If you start working with them
        again later, you can reopen the client and pick up where you
        left off. See{' '}
        <Link to="/support/archiving-and-reopening-clients">Archiving and reopening clients</Link>.
      </P>

      <H>How clients count towards billing</H>
      <P>
        Only <strong>active</strong> clients count towards your plan's
        client total on the <strong>per client plan</strong>. Archived
        clients don't. Your bill is calculated from your active client
        count on the <strong>28th of each month</strong>, so changes
        you make mid-month take effect on the next billing cycle.
      </P>
      <P>
        There is no pro-rating: a client added on the 27th and a client
        added on the 1st are billed identically on the upcoming 28th. If
        you're on the flat-rate plan (unlimited clients), the client
        count doesn't affect your bill at all.
      </P>

      <Callout variant="info" title="Test on yourself first">
        Before adding real clients, consider adding yourself as a test
        client using a different email address (your personal one works
        fine). Send yourself a request and walk through it from the
        client side. It's the fastest way to learn what your real clients
        will experience.
      </Callout>

      <H>Next step</H>
      <P>
        Once you've added a client, you're ready to send your first
        request. See{' '}
        <Link to="/support/sending-your-first-request">Sending your first receipt request</Link>.
      </P>
    </>
  ),
}
